import { API_BASE_URL } from '../../config'
import {
  FETCH_APPOINTMENTS_REQUEST,
  FETCH_APPOINTMENTS_SUCCESS,
  FETCH_APPOINTMENTS_FAILURE,
  UPDATE_APPOINTMENT,
  ADD_APPOINTMENT
} from './appointmentsActionTypes'

// Plain (non-thunk) action creators for events arriving over the
// appointments WebSocket — dispatched directly by the socket's message
// handler, not by an API call.
export const applyAppointmentUpdate = (payload) => ({ type: UPDATE_APPOINTMENT, payload })
export const addAppointment = (payload) => ({ type: ADD_APPOINTMENT, payload })

export const fetchAppointments = () => async (dispatch) => {
  const token = localStorage.getItem('auth_token')

  if (!token) {
    dispatch({ type: FETCH_APPOINTMENTS_FAILURE, error: 'No authentication token found' })
    return
  }

  dispatch({ type: FETCH_APPOINTMENTS_REQUEST })

  try {
    const res = await fetch(`${API_BASE_URL}/appointments/`, {
      headers: {
        'accept': 'application/json',
        'Authorization': `Token ${token}`
      },
      credentials: 'include'
    })

    if (!res.ok) {
      const errorData = await res.json().catch(() => ({}))
      throw new Error(errorData.detail || 'Failed to fetch appointments')
    }

    const data = await res.json()
    dispatch({ type: FETCH_APPOINTMENTS_SUCCESS, payload: data })
  } catch (error) {
    dispatch({
      type: FETCH_APPOINTMENTS_FAILURE,
      error: error.message || 'An error occurred while fetching appointments'
    })
  }
}

// Owner-side moderation actions on incoming appointment requests. Each
// updates the item in place (rather than refetching the whole list) so the
// UI doesn't flash a full-page spinner over a list the user is looking at.

async function postAppointmentAction(id, action, body) {
  const token = localStorage.getItem('auth_token')
  const res = await fetch(`${API_BASE_URL}/appointments/${id}/${action}/`, {
    method: 'POST',
    headers: {
      'accept': 'application/json',
      ...(body ? { 'Content-Type': 'application/json' } : {}),
      'Authorization': `Token ${token}`,
    },
    credentials: 'include',
    ...(body ? { body: JSON.stringify(body) } : {}),
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    const message = data.scheduled_time?.[0] || data.detail || `Failed to ${action} appointment`
    throw new Error(message)
  }
  return data
}

export const acceptAppointment = (id) => async (dispatch) => {
  await postAppointmentAction(id, 'accept')
  dispatch({ type: UPDATE_APPOINTMENT, payload: { id, changes: { is_accepted: true } } })
}

export const declineAppointment = (id, reason) => async (dispatch) => {
  const trimmed = (reason || '').trim()
  const data = await postAppointmentAction(id, 'cancel', trimmed ? { decline_reason: trimmed } : undefined)
  dispatch({
    type: UPDATE_APPOINTMENT,
    payload: { id, changes: { is_cancelled: true, decline_reason: data.decline_reason ?? trimmed ?? null } }
  })
}

export const completeAppointment = (id) => async (dispatch) => {
  await postAppointmentAction(id, 'complete')
  dispatch({ type: UPDATE_APPOINTMENT, payload: { id, changes: { is_completed: true } } })
}

export const rescheduleAppointment = (id, scheduledTimeIso, reason) => async (dispatch) => {
  const trimmed = (reason || '').trim()
  const data = await postAppointmentAction(id, 'reschedule', {
    scheduled_time: scheduledTimeIso,
    ...(trimmed ? { reschedule_reason: trimmed } : {})
  })
  dispatch({
    type: UPDATE_APPOINTMENT,
    payload: { id, changes: { scheduled_time: data.scheduled_time, reschedule_reason: data.reschedule_reason ?? trimmed ?? null } }
  })
}
